import React from 'react';
import { MdClose, MdFolderShared, MdSettings, MdSwapHoriz } from 'react-icons/md';
import './CompanyWorkspaceIntroModal.css';

function buildCompanyInitials(name) {
  const words = String(name || 'Company')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^\w\s&]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const initials = words
    .filter((word) => !['and', 'the', 'of', 'llc', 'inc', 'co', 'company'].includes(word.toLowerCase()))
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
  return initials || words[0]?.slice(0, 2).toUpperCase() || 'C';
}

export default function CompanyWorkspaceIntroModal({
  isOpen,
  companyName = 'your company',
  onClose,
  onDone,
}) {
  if (!isOpen) return null;

  const displayCompanyName = companyName || 'your company';
  const companyInitials = buildCompanyInitials(displayCompanyName);

  return (
    <div className="company-workspace-intro-overlay" role="presentation" onClick={onClose}>
      <div
        className="company-workspace-intro-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="company-workspace-intro-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="company-workspace-intro-header">
          <div>
            <div className="company-workspace-intro-eyebrow">Company Workspace</div>
            <h2 id="company-workspace-intro-title">How shared Atlas work works</h2>
            <p>
              Switch between private work and shared company work from the top-left workspace control.
            </p>
          </div>
          <button
            type="button"
            className="company-workspace-intro-close"
            onClick={onClose}
            aria-label="Close company workspace guide"
          >
            <MdClose size={20} />
          </button>
        </div>

        <div className="company-workspace-intro-switch-demo" aria-label="Personal and company workspace switch example">
          <span className="company-workspace-intro-switch-label personal">Personal</span>
          <div className="company-workspace-intro-switch">
            <span className="company-workspace-intro-switch-option personal">P</span>
            <span className="company-workspace-intro-switch-thumb" />
            <span className="company-workspace-intro-switch-option company">{companyInitials}</span>
          </div>
          <span className="company-workspace-intro-switch-label company">{displayCompanyName}</span>
        </div>

        <div className="company-workspace-intro-body">
          <section className="company-workspace-intro-panel">
            <div className="company-workspace-intro-panel-icon">
              <MdFolderShared size={20} />
            </div>
            <div>
              <h3>When you are in Company Workspace</h3>
              <ul>
                <li>Chats, uploaded files, and generated files are company-accessible.</li>
                <li>Files land in the company file browser, where Atlas can organize and retrieve them.</li>
                <li>Company Work History shows recent activity across the team, and Atlas can use that context.</li>
                <li>Atlas can pull company files into a conversation when you ask for them.</li>
              </ul>
            </div>
          </section>

          <section className="company-workspace-intro-panel">
            <div className="company-workspace-intro-panel-icon">
              <MdSwapHoriz size={20} />
            </div>
            <div>
              <h3>When you are in Personal Workspace</h3>
              <ul>
                <li>Your personal chats and files stay private to you.</li>
                <li>You can still ask Atlas to pull a company file into your personal conversation.</li>
                <li>You can ask Atlas to send selected personal files up to the company workspace.</li>
                <li>Use company mode when the work itself should become shared team context.</li>
              </ul>
            </div>
          </section>

          <section className="company-workspace-intro-panel">
            <div className="company-workspace-intro-panel-icon">
              <MdSettings size={20} />
            </div>
            <div>
              <h3>Settings you control</h3>
              <ul>
                <li>Click the user icon in the bottom-left to open User Settings.</li>
                <li>There you can choose whether Atlas starts in company mode by default.</li>
                <li>You can also choose whether Atlas may use your personal memories while you are in company mode.</li>
                <li>Company mode does not make your personal chats or personal files visible to other members.</li>
              </ul>
            </div>
          </section>
        </div>

        <div className="company-workspace-intro-footer">
          <button
            type="button"
            className="company-workspace-intro-primary"
            onClick={onDone}
          >
            <span>Got it</span>
          </button>
        </div>
      </div>
    </div>
  );
}
